import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  getDocs,
  getDoc,
  Query,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

export interface Message {
  id?: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderEmail: string;
  isAdmin: boolean;
  message: string;
  createdAt: any;
  attachments?: string[];
  // Product information if message is about a specific product
  productId?: string;
  productName?: string;
  productImage?: string;
  // Read/unread tracking
  isRead?: boolean;
  readAt?: any;
}

export interface Conversation {
  id?: string;
  userId: string;
  userEmail: string;
  userName: string;
  lastMessage?: string;
  lastMessageTime?: any;
  unreadCount?: number;
  createdAt: any;
  // Track which user has unread messages
  adminUnreadCount?: number;
  userUnreadCount?: number;
}

export const useMessages = (conversationId?: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      // console.log('useMessages: no conversationId provided');
      setMessages([]);
      setLoading(false);
      return;
    }

    // console.log('useMessages: Setting up listener for conversationId:', conversationId);
    setLoading(true);
    setError(null);

    try {
      const q = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId),
        orderBy('createdAt', 'asc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot<DocumentData>) => {
          // console.log('useMessages: onSnapshot fired, message count:', snapshot.size);
          const msgs: Message[] = [];
          snapshot.forEach((doc) => {
            // console.log('useMessages: Found message:', doc.id, doc.data().message);
            msgs.push({
              id: doc.id,
              ...doc.data(),
            } as Message);
          });
          // console.log('useMessages: Setting', msgs.length, 'messages');
          setMessages(msgs);
          setLoading(false);
        },
        (err: any) => {
          console.error('useMessages: Error in onSnapshot:', err);
          setError(err.message || 'Error loading messages');
          setLoading(false);
          setMessages([]);
        }
      );

      // console.log('useMessages: Listener attached successfully');
      return () => {
        // console.log('useMessages: Cleaning up listener for conversationId:', conversationId);
        unsubscribe();
      };
    } catch (err: any) {
      console.error('useMessages: Error setting up messages query:', err);
      setError(err.message || 'Error loading messages');
      setLoading(false);
      setMessages([]);
    }
  }, [conversationId]);

  const sendMessage = async (
    message: string,
    conversationId: string,
    senderId: string,
    senderName: string,
    senderEmail: string,
    isAdmin: boolean,
    productId?: string,
    productName?: string,
    productImage?: string
  ) => {
    try {
      console.log('sendMessage called with conversationId:', conversationId);
      
      // First, add the message
      await addDoc(collection(db, 'messages'), {
        conversationId,
        senderId,
        senderName,
        senderEmail,
        isAdmin,
        message,
        createdAt: serverTimestamp(),
        isRead: false, // Mark as unread by default
        ...(productId && { productId }),
        ...(productName && { productName }),
        ...(productImage && { productImage }),
      });

      // Update conversation with last message and timestamp
      console.log('Updating conversation metadata...');
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      const currentConvData = conversationDoc.data() || {};

      const updateData: any = {
        lastMessage: message,
        lastMessageTime: serverTimestamp(),
      };

      if (isAdmin) {
        // Admin sent message, increment user's unread count
        updateData.userUnreadCount = (currentConvData.userUnreadCount || 0) + 1;
        updateData.adminUnreadCount = 0;
      } else {
        // User sent message, increment admin's unread count
        updateData.adminUnreadCount = (currentConvData.adminUnreadCount || 0) + 1;
        updateData.userUnreadCount = 0;
      }

      await updateDoc(conversationRef, updateData);
      console.log('Conversation updated successfully');
    } catch (err: any) {
      console.error('sendMessage error details:', {
        code: err.code,
        message: err.message,
        fullError: err,
      });
      throw new Error(err.message || 'Failed to send message');
    }
  };

  const markMessagesAsRead = async (conversationId: string, userId: string, isAdmin: boolean) => {
    try {
      // Get all unread messages from the other user
      const q = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId),
        where('senderId', '!=', userId)
      );

      const snapshot = await getDocs(q);
      const batch: Promise<void>[] = [];

      snapshot.forEach((doc) => {
        const msgData = doc.data();
        if (!msgData.isRead) {
          batch.push(
            updateDoc(doc.ref, {
              isRead: true,
              readAt: serverTimestamp(),
            })
          );
        }
      });

      await Promise.all(batch);

      // Update conversation unread count
      const conversationRef = doc(db, 'conversations', conversationId);
      const updateData: any = {};
      
      if (isAdmin) {
        updateData.adminUnreadCount = 0;
      } else {
        updateData.userUnreadCount = 0;
      }

      await updateDoc(conversationRef, updateData);
      console.log('Marked messages as read for conversation:', conversationId);
    } catch (err: any) {
      console.error('Error marking messages as read:', err);
    }
  };

  return { messages, loading, error, sendMessage, markMessagesAsRead };
};

export const useConversations = (userId?: string, isAdmin?: boolean) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setConversations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let q: Query<DocumentData>;

      if (isAdmin) {
        // Admins see all conversations - ordered by last message time
        q = query(
          collection(db, 'conversations'),
          orderBy('lastMessageTime', 'desc')
        );
      } else {
        // Users see only their conversations
        q = query(
          collection(db, 'conversations'),
          where('userId', '==', userId),
          orderBy('lastMessageTime', 'desc')
        );
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot<DocumentData>) => {
          const convs: Conversation[] = [];
          snapshot.forEach((doc) => {
            convs.push({
              id: doc.id,
              ...doc.data(),
            } as Conversation);
          });

          // Sort: unread conversations first, then by last message time
          convs.sort((a, b) => {
            const aUnread = isAdmin ? (a.adminUnreadCount || 0) : (a.userUnreadCount || 0);
            const bUnread = isAdmin ? (b.adminUnreadCount || 0) : (b.userUnreadCount || 0);

            // If unread counts are different, prioritize unread
            if (aUnread !== bUnread) {
              return bUnread - aUnread;
            }

            // Otherwise sort by last message time
            const aTime = a.lastMessageTime?.toMillis?.() || 0;
            const bTime = b.lastMessageTime?.toMillis?.() || 0;
            return bTime - aTime;
          });

          setConversations(convs);
          setLoading(false);
        },
        (err: any) => {
          console.error('Error loading conversations:', err);
          setError(err.message || 'Error loading conversations');
          setLoading(false);
          // Set empty array so UI doesn't stay stuck loading
          setConversations([]);
        }
      );

      return () => unsubscribe();
    } catch (err: any) {
      console.error('Error setting up conversation query:', err);
      setError(err.message || 'Error loading conversations');
      setLoading(false);
      setConversations([]);
    }
  }, [userId, isAdmin]);

  const createConversation = async (
    userId: string,
    userEmail: string,
    userName: string
  ) => {
    try {
      // console.log('createConversation called with userId:', userId);
      
      // First, check if conversation already exists for this user
      const existingQ = query(
        collection(db, 'conversations'),
        where('userId', '==', userId)
      );
      
      const existingSnapshot = await getDocs(existingQ);
      
      if (!existingSnapshot.empty) {
        // Conversation already exists, return the first one
        const existingConversationId = existingSnapshot.docs[0].id;
        // console.log('Existing conversation found with ID:', existingConversationId);
        return existingConversationId;
      }
      
      // No existing conversation, create a new one
      const docRef = await addDoc(collection(db, 'conversations'), {
        userId,
        userEmail,
        userName,
        createdAt: serverTimestamp(),
        lastMessageTime: serverTimestamp(),
      });
      // console.log('New conversation created with ID:', docRef.id);
      return docRef.id;
    } catch (err: any) {
      console.error('createConversation error details:', {
        code: err.code,
        message: err.message,
        fullError: err,
      });
      throw new Error(err.message || 'Failed to create conversation');
    }
  };

  return { conversations, loading, error, createConversation };
};
